'use client'

import { useGlobalSearch } from '@/components/provider/global-search-provider'
import { NavSubmenuCard } from '@/components/shared/layout/nav-submenu-card'
import { SignInButton } from '@/components/shared/sign-in-button'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { isMarketplaceEnabled } from '@/lib/flags'
import {
  GlobalNavStoryblok,
  GlobalNavSubMenuCaseStudyStoryblok,
  GlobalNavSubMenuColCardStoryblok,
  GlobalNavSubMenuColLatestReleaseStoryblok,
  GlobalNavSubMenuColLinksStoryblok,
  type GlobalNavItemStoryblok,
} from '@/lib/types/component-types-sb'
import { IconMark } from '@vendure/brand'
import { Slant as Hamburger } from 'hamburger-react'
import { ChevronLeft, ChevronRight, SearchIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { ElementType, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { SiGithub } from 'react-icons/si'
import { NavSubMenuCaseStudy } from './nav-sub-menu-case-study'
import { NavSubMenuLatestRelease } from './nav-sub-menu-latest-release'

export function MobileNavItem({
  item,
  onClick,
}: {
  item: GlobalNavItemStoryblok
  onClick: (item: GlobalNavItemStoryblok) => void
}) {
  const hasSubMenu = item.cols && item.cols.length > 0
  const Tag = hasSubMenu || item.comingSoon ? ('div' as ElementType) : Link

  return (
    <Tag
      {...(hasSubMenu
        ? { onClick: () => onClick(item) }
        : {
            href: item.comingSoon
              ? null
              : item.target?.linktype === 'url'
                ? item.target?.url
                : '/' + item.target?.cached_url,
          })}
      className={
        'border-border flex w-full items-center justify-between border-b px-6 py-3 last:border-b-0'
      }
    >
      <span className={'text-accent-foreground text-2xl font-medium'}>{item.label}</span>
      {item.comingSoon && (
        <Badge
          variant="brand"
          className="text-xs"
        >
          Coming Soon
        </Badge>
      )}
      {item.cols && item.cols.length > 0 && <ChevronRight className="text-muted-foreground" />}
    </Tag>
  )
}

export function MobileNav({ content }: { content: GlobalNavStoryblok }) {
  const [isOpen, setOpen] = useState(false)

  const navbarRef = useRef<HTMLDivElement>(null)
  const [subMenu, setSubMenu] = useState<GlobalNavItemStoryblok | null>(null)
  const pathname = usePathname()
  const { searchOpen, onOpenSearch } = useGlobalSearch()

  useEffect(() => {
    setOpen(false)
    setSubMenu(null)
  }, [pathname])

  useEffect(() => {
    setOpen(false)
    setSubMenu(null)
  }, [searchOpen])

  const subMenuLinks = useMemo(() => {
    return subMenu?.cols
      ?.filter((c) => c.component === 'global-nav-sub-menu-col-links')
      .map((c) => c as GlobalNavSubMenuColLinksStoryblok)
      .map((c) => c.links ?? [])
      .flat()
  }, [subMenu?._uid])

  const subMenuCards = useMemo(() => {
    return subMenu?.cols
      ?.filter((c) => c.component === 'global-nav-sub-menu-col-card')
      .map((c) => c as GlobalNavSubMenuColCardStoryblok)
      .flat()
  }, [subMenu?._uid])

  const subMenuLatestRelease = useMemo(() => {
    return subMenu?.cols
      ?.filter((c) => c.component === 'global-nav-sub-menu-col-latest-release')
      .map((c) => c as GlobalNavSubMenuColLatestReleaseStoryblok)
      .flat()
  }, [subMenu?._uid])

  const subMenuCaseStudy = useMemo(() => {
    return subMenu?.cols
      ?.filter((c) => c.component === 'global-nav-sub-menu-case-study')
      .map((c) => c as GlobalNavSubMenuCaseStudyStoryblok)
      .flat()
  }, [subMenu?._uid])

  return (
    <>
      <div
        ref={navbarRef}
        className="bg-background border-border fixed top-0 right-0 left-0 z-50 flex h-(--height-navbar) w-full items-center justify-between border-b ps-6 pe-4"
      >
        <Link href={'/'}>
          <IconMark className={'text-brand h-7 w-auto'} />
        </Link>
        <div className={'flex items-center gap-2.5'}>
          <Button
            onClick={() => onOpenSearch()}
            size={'icon'}
            variant={'secondary'}
          >
            <SearchIcon />
          </Button>
          <Hamburger
            size={20}
            easing="ease-in-out"
            distance="lg"
            toggled={isOpen}
            toggle={() => {
              setOpen(!isOpen)
              setSubMenu(null)
            }}
            hideOutline
          />
        </div>
      </div>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={
              'bg-background fixed top-0 right-0 bottom-0 left-0 z-40 h-dvh w-full overflow-y-scroll pt-(--height-navbar)'
            }
          >
            <div className={'relative h-full w-full'}>
              <AnimatePresence initial={false}>
                {!subMenu && (
                  <motion.div
                    key={content._uid}
                    initial={{
                      translateX: '-100%',
                    }}
                    animate={{
                      translateX: 0,
                    }}
                    exit={{
                      translateX: '-100%',
                    }}
                    className={'absolute top-0 left-0 w-full'}
                    transition={{ ease: 'easeInOut' }}
                  >
                    {content.items?.map((item) => (
                      <MobileNavItem
                        key={item._uid}
                        item={item}
                        onClick={setSubMenu}
                      />
                    ))}
                    <Suspense>
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="space-y-3.5 p-6"
                      >
                        <Button
                          asChild
                          className={'w-full'}
                        >
                          <Link
                            href={`https://github.com/${process.env.NEXT_PUBLIC_GITHUB_REPOSITORY}`}
                          >
                            <SiGithub /> GitHub
                          </Link>
                        </Button>
                        {isMarketplaceEnabled && <SignInButton className={'w-full'} />}
                      </motion.div>
                    </Suspense>
                  </motion.div>
                )}
                {subMenu && (
                  <motion.div
                    key={subMenu._uid}
                    initial={{
                      translateX: '100%',
                    }}
                    animate={{
                      translateX: '0%',
                    }}
                    exit={{
                      translateX: '100%',
                    }}
                    className={'absolute top-0 left-0 w-full'}
                    transition={{ ease: 'easeInOut' }}
                  >
                    <div
                      className={'border-border bg-sidebar border-b px-6 py-2.5'}
                      onClick={() => setSubMenu(null)}
                    >
                      <Button
                        variant={'ghost'}
                        className={'ps-0'}
                      >
                        <ChevronLeft /> Back
                      </Button>
                    </div>
                    {subMenuLinks?.map((item: GlobalNavItemStoryblok) => (
                      <MobileNavItem
                        key={item._uid}
                        item={item}
                        onClick={setSubMenu}
                      />
                    ))}
                    <div className={'space-y-2.5 p-6'}>
                      {subMenuCards?.map((item) => (
                        <div
                          key={item._uid}
                          className={'bg-sidebar border-border rounded-lg border p-4'}
                        >
                          <NavSubmenuCard
                            className={'px-0'}
                            col={item}
                          />
                        </div>
                      ))}
                      {subMenuLatestRelease?.map((item) => (
                        <div
                          key={item._uid}
                          className={'bg-sidebar border-border rounded-lg border px-4'}
                        >
                          <NavSubMenuLatestRelease col={item} />
                        </div>
                      ))}
                      {subMenuCaseStudy?.map((item) => (
                        <div
                          key={item._uid}
                          className={'bg-sidebar border-border rounded-lg border p-4'}
                        >
                          <NavSubMenuCaseStudy
                            className={'px-0'}
                            col={item}
                          />
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
